import React, { useCallback, useEffect, useState } from 'react';
import {
  Calendar,
  Search,
  X,
  Loader,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  MapPin,
  Package,
  Truck,
  User
} from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { purchaseOrderHistoryService } from '../../../../services/purchaseOrderHistoryService';
import { ReceivedPurchaseOrderDto, ReceivedPurchaseOrderLineDto } from '../../../../types/purchaseOrderHistory';
import { ScanTextInput } from '../../../common/inputs';

/**
 * Fanen «Innkjøp» i Historikk-modulen (TW 36403717, Haucon).
 *
 * Operatøren står med varer som ble mottatt på en stor mottakslokasjon og skal plassere dem på hylla.
 * I stedet for et utskrevet papir slår hen opp innkjøpet her og ser lokasjonen fra innkjøpslinjen
 * (StdLocationName) stort og tydelig. Faktisk mottakslokasjon vises som liten merket tilleggslinje
 * når den finnes og avviker.
 *
 * Utvalg: innkjøpsordre med minst ett mottak, filtrert på siste mottaksdato, 14 dager som standard,
 * uavhengig av ordrestatus. Følger valgt lager. Søk på innkjøpsnr, leverandør og varenr.
 */
const toIsoDate = (d: Date) => d.toISOString().split('T')[0];

const formatDateTime = (value?: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatQty = (value?: number | null) => {
  if (value === null || value === undefined) return '-';
  return Number.isInteger(value) ? value.toString() : value.toFixed(2);
};

export const ReceivedPurchaseOrders: React.FC = () => {
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouse();

  const [orders, setOrders] = useState<ReceivedPurchaseOrderDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return toIsoDate(d);
  });
  const [dateTo, setDateTo] = useState<string>(() => toIsoDate(new Date()));
  const [searchText, setSearchText] = useState('');
  const [expandedOrderNo, setExpandedOrderNo] = useState<number | null>(null);

  const load = useCallback(async (search: string) => {
    setLoading(true);
    setError('');
    try {
      const data = await purchaseOrderHistoryService.getReceivedHistory({
        fromDate: dateFrom || undefined,
        toDate: dateTo || undefined,
        warehouse: currentWarehouse || undefined,
        search: search || undefined
      });
      setOrders(data);
      // Ett treff → åpne det med en gang (typisk når operatøren har søkt på et konkret innkjøp)
      setExpandedOrderNo(data.length === 1 ? data[0].orderNo : null);
    } catch (err) {
      console.error('Error loading received purchase orders:', err);
      setError(t('warehouse.modules.history.receivedPurchases.loadFailed', {}, 'Kunne ikke hente mottatte innkjøp'));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, currentWarehouse, t]);

  // Første lasting + ved lagerbytte. Dato-/søkeendringer kjøres eksplisitt med Søk-knappen/Enter.
  useEffect(() => {
    load(searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWarehouse]);

  const handleSearch = () => load(searchText);

  const handleReset = () => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    setDateFrom(toIsoDate(d));
    setDateTo(toIsoDate(new Date()));
    setSearchText('');
    // Lastes med nullstilte verdier i neste tick (state er ikke oppdatert ennå her)
    setTimeout(() => load(''), 0);
  };

  const toggleExpand = (orderNo: number) => {
    setExpandedOrderNo(prev => (prev === orderNo ? null : orderNo));
  };

  const renderLine = (line: ReceivedPurchaseOrderLineDto) => {
    const location = (line.stdLocationName || '').trim();
    const receivedAt = (line.receivedBinCodes || '').trim();
    const showReceivedAt = !!receivedAt && receivedAt.toLowerCase() !== location.toLowerCase();
    const unit = line.itemUnitTxt ? ` ${line.itemUnitTxt}` : '';

    return (
      <div key={`${line.orderNo}-${line.lineNo}`} className="bg-white rounded p-3 border border-gray-200">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-sm font-semibold text-gray-900">{line.itemNo}</div>
            <div className="text-sm text-gray-700 break-words">{line.description1 || '-'}</div>
            <div className="text-xs text-gray-500 mt-1">
              {t('warehouse.modules.history.receivedPurchases.received', {}, 'Mottatt')}: {formatQty(line.quantityDelivered)}
              {' / '}
              {t('warehouse.modules.history.receivedPurchases.ordered', {}, 'Bestilt')}: {formatQty(line.quantity)}{unit}
            </div>
          </div>
          {/* Lokasjon fra innkjøpsordren — hovedinformasjonen, stor og fet */}
          <div className="text-right flex-shrink-0">
            {location ? (
              <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-900 text-xl font-bold tracking-wide">
                <MapPin className="h-5 w-5 text-blue-600" />
                {location}
              </div>
            ) : (
              <div className="text-xs text-gray-400 italic">
                {t('warehouse.modules.history.receivedPurchases.noLocation', {}, 'Ingen lokasjon på innkjøpslinjen')}
              </div>
            )}
            {showReceivedAt && (
              <div className="text-[11px] text-gray-500 mt-1">
                {t('warehouse.modules.history.receivedPurchases.receivedAt', {}, 'Mottatt på')}: <span className="font-mono">{receivedAt}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 max-w-6xl mx-auto space-y-4">
      {/* Filter */}
      <div className="bg-white rounded-lg p-4 shadow-sm border">
        <h2 className="font-semibold mb-3 flex items-center">
          <Calendar className="h-5 w-5 mr-2 text-blue-500" />
          {t('common.buttons.filter')}
        </h2>

        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.modules.history.receivedPurchases.fromDate', {}, 'Mottatt fra')}
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.modules.history.receivedPurchases.toDate', {}, 'Mottatt til')}
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="relative">
            <ScanTextInput
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              placeholder={t('warehouse.modules.history.receivedPurchases.searchPlaceholder', {}, 'Søk innkjøpsnr, leverandør eller varenr...')}
              className="w-full pl-10 pr-10 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            {searchText && (
              <button
                type="button"
                onClick={() => setSearchText('')}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={handleSearch}
              disabled={loading}
              className="px-4 py-2 min-h-[44px] bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>{t('common.status.loading')}</span>
                </>
              ) : (
                <>
                  <Search className="h-4 w-4" />
                  <span>{t('common.buttons.search')}</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 min-h-[44px] border border-gray-300 rounded hover:bg-gray-50 flex items-center space-x-2"
            >
              <X className="h-4 w-4" />
              <span>{t('common.buttons.reset')}</span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Liste */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b">
          <h2 className="font-semibold flex items-center">
            <Truck className="h-5 w-5 mr-2 text-blue-500" />
            {t('warehouse.modules.history.receivedPurchases.title', {}, 'Mottatte innkjøp')} ({orders.length})
          </h2>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Loader className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-2" />
            <p className="text-gray-500">{t('warehouse.modules.history.receivedPurchases.loading', {}, 'Henter mottatte innkjøp...')}</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>{t('warehouse.modules.history.receivedPurchases.noResults', {}, 'Ingen innkjøp med mottak i perioden')}</p>
            <p className="text-sm">{t('warehouse.modules.history.receivedPurchases.tryAdjusting', {}, 'Endre datoer eller søk')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {orders.map((order) => {
              const isExpanded = expandedOrderNo === order.orderNo;
              const orderLabel = order.orderNoERP || `#${order.orderNo}`;
              return (
                <div key={order.orderNo}>
                  <button
                    type="button"
                    className="w-full text-left p-4 hover:bg-gray-50 min-h-[44px]"
                    onClick={() => toggleExpand(order.orderNo)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="pt-1">
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-gray-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
                          <div className="font-semibold text-lg text-gray-900 truncate">{order.supplierName || order.supplierNo || '-'}</div>
                          <span className="font-mono text-sm bg-gray-100 text-gray-800 px-2 py-0.5 rounded">{orderLabel}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                          <span className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {t('warehouse.modules.history.receivedPurchases.lastReceipt', {}, 'Siste mottak')}: {formatDateTime(order.lastReceiptDate) || '-'}
                          </span>
                          <span className="flex items-center">
                            <Package className="h-4 w-4 mr-1" />
                            {t('warehouse.modules.history.receivedPurchases.linesCount', { '0': `${order.lineCount}` }, `${order.lineCount} linjer`)}
                          </span>
                          {order.purchaserRef && (
                            <span className="flex items-center">
                              <User className="h-4 w-4 mr-1" />
                              {t('warehouse.modules.history.receivedPurchases.purchaser', {}, 'Innkjøper')}: {order.purchaserRef}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
                      {order.lines.length === 0 ? (
                        <div className="text-sm text-gray-500 text-center py-4">
                          {t('warehouse.modules.history.receivedPurchases.noResults', {}, 'Ingen innkjøp med mottak i perioden')}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {order.lines.map(renderLine)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceivedPurchaseOrders;
